import { POI } from '../poi/POI';
import { EventManager } from '../core/EventManager';
import { Logger } from '../core/Logger';

/**
 * 📍 POIDialogManager - Handles POI list dialog functionality
 * Shows a list of all POIs with their order, names, and information
 */
export class POIDialogManager {
    private poiDialog: HTMLElement | null = null;

    constructor(
        private eventManager: EventManager,
        private getPOIs: () => POI[],
        private getActivePOI: () => POI | null = () => null
    ) {
        // Remove the duplicate F7 key listener - InputManager handles this
        console.log('🔍 POIDialogManager: Initialized (F7 handling delegated to InputManager)');
        
        // Add a test F7 listener to see if the key is being detected at all
        document.addEventListener('keydown', (event) => {
            if (event.key === 'F7') {
                console.log('🔍 POIDialogManager: F7 key detected globally (this should not handle it, just testing)');
            }
        });
    }

    /**
     * Toggle POI dialog visibility
     */
    public togglePOIDialog(): void {
        console.log('🔍 POIDialogManager: togglePOIDialog called, current dialog state:', this.poiDialog ? 'open' : 'closed');
        if (this.poiDialog) {
            this.hidePOIDialog();
        } else {
            this.showPOIDialog();
        }
    }

    /**
     * Show POI dialog with list of all POIs
     */
    private showPOIDialog(): void {
        console.log('🔍 POIDialogManager: showPOIDialog called');
        if (this.poiDialog) {
            console.log('🔍 POIDialogManager: Dialog already exists, returning early');
            return;
        }

        console.log('🔍 POIDialogManager: Creating new dialog element');
        // Create POI dialog
        this.poiDialog = document.createElement('div');
        this.poiDialog.style.cssText = `
            position: fixed;
            top: 50px;
            right: 50px;
            width: 600px;
            max-width: 90vw;
            max-height: 80vh;
            background: linear-gradient(135deg, 
                rgba(15, 35, 15, 0.98) 0%,
                rgba(25, 45, 25, 0.95) 25%,
                rgba(35, 55, 35, 0.95) 50%,
                rgba(25, 45, 25, 0.95) 75%,
                rgba(15, 35, 15, 0.98) 100%
            );
            border-radius: 12px;
            backdrop-filter: blur(20px) saturate(180%);
            border: 1px solid rgba(0, 255, 0, 0.3);
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.7);
            color: white;
            font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
            font-size: 12px;
            z-index: 15000;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        `;

        // Header
        const header = this.createDialogHeader();
        
        // Content
        const content = this.createDialogContent();

        this.poiDialog.appendChild(header);
        this.poiDialog.appendChild(content);

        // Add to DOM
        document.body.appendChild(this.poiDialog);
        console.log('🔍 POIDialogManager: Dialog added to DOM, checking if visible');
        console.log('🔍 POIDialogManager: Dialog element:', this.poiDialog);
        console.log('🔍 POIDialogManager: Dialog computed style:', window.getComputedStyle(this.poiDialog).display);

        //Logger.Instance.Info('📍 POI List Dialog opened - showing all POIs', 'POI');
    }

    /**
     * Create dialog header with title and close button
     */
    private createDialogHeader(): HTMLElement {
        const header = document.createElement('div');
        header.style.cssText = `
            padding: 15px 20px;
            background: rgba(0, 0, 0, 0.3);
            border-bottom: 1px solid rgba(0, 255, 0, 0.2);
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            font-weight: bold;
        `;

        const title = document.createElement('span');
        title.textContent = '📍 POI List - F7 to toggle';
        title.style.cssText = `
            background: linear-gradient(45deg, #32CD32, #228B22, #00FF00);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        `;

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '✕';
        closeBtn.style.cssText = `
            background: none;
            border: none;
            color: rgba(255, 255, 255, 0.7);
            font-size: 18px;
            cursor: pointer;
            padding: 0;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
        `;
        closeBtn.addEventListener('click', () => this.hidePOIDialog());

        header.appendChild(title);
        header.appendChild(closeBtn);

        return header;
    }

    /**
     * Create dialog content with POI list
     */
    private createDialogContent(): HTMLElement {
        const content = document.createElement('div');
        content.style.cssText = `
            padding: 20px;
            overflow-y: auto;
            flex: 1;
            line-height: 1.4;
        `;

        // Generate POI list content
        content.innerHTML = this.generatePOIListContent();

        return content;
    }

    /**
     * Generate HTML content for POI list
     */
    private generatePOIListContent(): string {
        const pois = this.getPOIs();
        console.log('🔍 POIDialogManager: generatePOIListContent called, POIs count:', pois.length);
        console.log('🔍 POIDialogManager: POIs data:', pois);
        
        if (pois.length === 0) {
            return `
                <div style="text-align: center; padding: 40px; color: rgba(255, 255, 255, 0.6);">
                    <div style="font-size: 48px; margin-bottom: 20px;">📍</div>
                    <div style="font-size: 16px; margin-bottom: 10px;">No POIs Found</div>
                    <div style="font-size: 12px;">Create some POIs to see them listed here</div>
                </div>
            `;
        }

        let html = `
            <div style="margin-bottom: 20px; padding: 10px; background: rgba(0, 255, 0, 0.1); border-radius: 8px; border-left: 4px solid #32CD32;">
                <div style="font-size: 14px; font-weight: bold; color: #32CD32;">📊 POI Summary</div>
                <div style="margin-top: 5px; color: rgba(255, 255, 255, 0.8);">
                    Total POIs: <span style="color: #00FF00; font-weight: bold;">${pois.length}</span>
                </div>
            </div>
        `;

        html += '<div style="margin-bottom: 15px; font-size: 14px; font-weight: bold; color: #32CD32;">📍 POI List (in order):</div>';

        pois.forEach((poi, index) => {
            const isActive = poi === this.getActivePOI();
            const title = poi.name || poi.id || `POI ${index + 1}`;
            const position = poi.transform.getWorldPosition();
            const shape = this.getPOIShape(poi);
            const color = this.getPOIColor(poi);

            html += `
                <div style="
                    margin-bottom: 12px; 
                    padding: 12px; 
                    background: ${isActive ? 'rgba(0, 255, 0, 0.15)' : 'rgba(255, 255, 255, 0.05)'}; 
                    border-radius: 8px; 
                    border-left: 4px solid ${isActive ? '#00FF00' : '#32CD32'};
                    ${isActive ? 'box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);' : ''}
                ">
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                        <div style="font-weight: bold; color: ${isActive ? '#00FF00' : '#32CD32'};">
                            ${isActive ? '🎯 ' : ''}#${index + 1} - ${title}
                        </div>
                        <div style="font-size: 10px; color: rgba(255, 255, 255, 0.6);">
                            ${poi.id}
                        </div>
                    </div>
                    
                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 11px;">
                        <div>
                            <span style="color: rgba(255, 255, 255, 0.7);">Shape:</span> 
                            <span style="color: #90EE90;">${shape}</span>
                        </div>
                        <div>
                            <span style="color: rgba(255, 255, 255, 0.7);">Color:</span> 
                            <span style="color: ${color};">●</span> <span style="color: #90EE90;">${color}</span>
                        </div>
                        <div style="grid-column: 1 / -1;">
                            <span style="color: rgba(255, 255, 255, 0.7);">Position:</span> 
                            <span style="color: #90EE90;">
                                (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)})
                            </span>
                        </div>
                    </div>
                    
                    ${isActive ? '<div style="margin-top: 8px; font-size: 10px; color: #00FF00;">● Currently Active POI</div>' : ''}
                </div>
            `;
        });

        return html;
    }



    /**
     * Get POI shape name
     */
    private getPOIShape(poi: POI): string {
        try {
            return poi.poidata?.shape?.value || 'unknown';
        } catch {
            return 'unknown';
        }
    }

    /**
     * Get POI color
     */
    private getPOIColor(poi: POI): string {
        try {
            const color = poi.poidata?.color?.value;
            if (color && typeof color === 'object' && 'toHex' in color) {
                return color.toHex();
            }
            return color || '#ffffff';
        } catch {
            return '#ffffff';
        }
    }

    /**
     * Hide POI dialog
     */
    private hidePOIDialog(): void {
        if (this.poiDialog) {
            document.body.removeChild(this.poiDialog);
            this.poiDialog = null;
            //Logger.Instance.Info('📍 POI List Dialog closed', 'POI');
        }
    }

    /**
     * Test method to manually trigger dialog (for debugging)
     */
    public testShowDialog(): void {
        console.log('🔍 POIDialogManager: testShowDialog called manually');
        this.showPOIDialog();
    }

    /**
     * Dispose of the POI dialog manager
     */
    public dispose(): void {
        this.hidePOIDialog();
    }
} 